import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import { BulkProposalCard } from './BulkProposalCard';
import { useFormatCurrency } from '@/hooks/use-format-currency';
import { MaintenanceAction, type MaintainInvoicesPayload } from './proposal-part.types';

interface Props {
  proposalId: string;
  payload: MaintainInvoicesPayload;
  resolved?: string | undefined;
  onConfirm: (proposalId: string) => void;
  onCancel: (proposalId: string) => void;
}

/** Static keys per operation, so the translations stay statically verifiable. */
const LABEL_KEYS = {
  [MaintenanceAction.REVALIDATE]: 'revalidate',
  [MaintenanceAction.ARCHIVE]: 'archive',
  [MaintenanceAction.UNARCHIVE]: 'unarchive'
} as const;

/**
 * Confirmation card for bulk invoice housekeeping. The operation is named in the title and
 * every affected invoice is listed — one card never mixes two different operations.
 */
export function MaintainInvoicesProposalCard({
  proposalId,
  payload,
  resolved,
  onConfirm,
  onCancel
}: Props) {
  const t = useFeatureTranslation('aiChat');
  const formatAmount = useFormatCurrency();
  const key = LABEL_KEYS[payload.action];

  return (
    <BulkProposalCard
      title={t(`proposals.maintain-invoices.${key}.title`)}
      description={t(`proposals.maintain-invoices.${key}.countDescription`, {
        count: payload.invoices.length
      })}
      proposalId={proposalId}
      rows={payload.invoices}
      rowKey={row => row.invoiceId}
      primary={row => (
        <>
          <span className='font-medium'>{row.invoiceNumber}</span>
          {row.supplier && <span className='text-foreground-muted'>{` · ${row.supplier}`}</span>}
        </>
      )}
      secondary={row => formatAmount(row.amount, row.currency) ?? '—'}
      resolved={resolved}
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
}
